import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { UpsellModalLayout } from '../upsell-modal-layout/upsell-modal-layout';
import { FeatureDescription } from './feature-description';
import { ConfirmFeatureUpsellButton } from '../confirm-upsell-button/confirm-feature-upsell-button';
import { UpsellModalImage } from '../modal-svgs/upsell-modal-image';
import { useLocalizedPlanName } from '../../../pages/settings/payment-and-subscription/components/plan-name-helper';
import { useFirstPlanWithIncludedFeature } from '../../../pages/settings/payment-and-subscription/hooks/use-plan-details';
import Global from '../../../store/global-selectors';
import { Workflows } from '../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';

import type { UpsellModalContentCode } from '../../../constants/upsell-modal-content-codes';

type FeatureUpsellModalProps = {
  upsellModalContentCode: UpsellModalContentCode;
  onClose: () => void;
  source: string;
};

export const FeatureUpsellModal = ({ upsellModalContentCode, onClose, source }: FeatureUpsellModalProps) => {
  const trackUpsellBillingPlanWorkflow = useTrackWorkflow(Workflows.UPSELL_BILLING_PLAN_FLOW);
  const planToUpsell = useFirstPlanWithIncludedFeature(upsellModalContentCode);
  const planToUpsellName = useLocalizedPlanName(planToUpsell?.name);
  const currentBillingPlanId = useSelector((state) => {
    const subscriptionData = Global.selectSellerSubscriptionData(state);
    return subscriptionData?.currentBillingPlan?.billingPlanId;
  });

  useOnComponentMount(() => {
    trackUpsellBillingPlanWorkflow(WorkflowSteps.START, {
      source,
      currentBillingPlanId,
      upsellBillingPlanId: planToUpsell?.billingPlanId,
    });
  });

  const onCancel = useCallback(() => {
    trackUpsellBillingPlanWorkflow(WorkflowSteps.CANCEL);
    onClose();
  }, [trackUpsellBillingPlanWorkflow, onClose]);

  const descriptionContent = (
    <FeatureDescription
      billingPlanId={planToUpsell?.billingPlanId ?? null}
      planName={planToUpsellName}
      upsellModalContentCode={upsellModalContentCode}
    />
  );

  const footerContent = <ConfirmFeatureUpsellButton onCloseModal={onClose} shouldTrackUpsell />;

  const imageContent = <UpsellModalImage upsellModalContentCode={upsellModalContentCode} />;

  return (
    <UpsellModalLayout
      descriptionContent={descriptionContent}
      footerContent={footerContent}
      imageContent={imageContent}
      onCancel={onCancel}
      onClose={onClose}
    />
  );
};
